import React, { useState, useCallback } from 'react';
import {
    Box,
    Modal,
    IconButton,
    CircularProgress,
    Alert,
    Button,
    Typography,
    useTheme
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';

export interface VenueMapData {
    imageUrl: string;
    venueName: string;
    venueId: number;
}

// API response types based on VenueMapDrawer pattern
export interface VenueMapResponse {
    venue: {
        name: string;
        static_map_image_url: string;
    };
}

export interface ApiErrorResponse {
    message?: string;
}

export interface VenueMapModalState {
    open: boolean;
    loading: boolean;
    error: string | null;
    mapData: VenueMapData | null;
}

export interface VenueMapModalProps {
    modalState: VenueMapModalState;
    onClose: () => void;
    onImageError: () => void;
}

/**
 * Custom hook for venue map handling functionality
 * Provides state management and API integration for venue map operations
 */
export const useVenueMapHandling = () => {
    const [modalState, setModalState] = useState<VenueMapModalState>({
        open: false,
        loading: false,
        error: null,
        mapData: null
    });
    const dataProvider = useDataProvider();

    /**
     * Handles and formats error messages with comprehensive type checking
     * Based on VenueMapDrawer error handling pattern
     */
    const handleApiError = useCallback((err: any): string => {
        if (typeof err === 'string') {
            return err;
        }

        if (err instanceof Error) {
            return err.message;
        }

        if (err && typeof err === 'object' && 'message' in err) {
            return (err as ApiErrorResponse).message || 'Unknown error occurred';
        }

        return 'Unknown error occurred';
    }, []);

    /**
     * Main venue map click handler function
     * Fetches venue map data and opens the modal
     *
     * @param venueId - The venue ID to fetch map data for
     */
    const handleVenueMapClick = useCallback(async (
        venueId: number,
        venueName?: string
    ): Promise<void> => {
        if (!venueId) {
            console.warn('handleVenueMapClick: venueId is required');
            return;
        }

        // Open modal and start loading
        setModalState({
            open: true,
            loading: true,
            error: null,
            mapData: null
        });

        try {
            // Use backend proxy to fetch VividSeats API data to avoid CORS issues
            // Following the same pattern as VenueMapDrawer component
            const response = await dataProvider.fetchData('RTP_Venues', `GetVenueMap?venueId=${venueId}`);

            // Extract map image URL from the backend response
            if (response && response.data && (response.data as VenueMapResponse).venue && (response.data as VenueMapResponse).venue.static_map_image_url) {
                const data = response.data as VenueMapResponse;

                setModalState({
                    open: true,
                    loading: false,
                    error: null,
                    mapData: {
                        imageUrl: data.venue.static_map_image_url,
                        venueName: data.venue.name || venueName || 'Unknown Venue',
                        venueId: venueId
                    }
                });
            } else {
                throw new Error('No map data available from backend proxy');
            }
        } catch (err) {
            console.error('Error fetching venue map:', err);

            const errorMessage = handleApiError(err);

            // Check if the error is due to missing backend endpoint
            let finalErrorMessage = `Failed to load venue map: ${errorMessage}`;
            if (errorMessage.includes('404') || errorMessage.includes('Not Found') || errorMessage.includes('GetVenueMap')) {
                finalErrorMessage = 'Backend proxy endpoint \'GetVenueMap\' is not yet implemented. Please contact your system administrator to enable venue map functionality.';
            }

            setModalState({
                open: true,
                loading: false,
                error: finalErrorMessage,
                mapData: null
            });
        }
    }, [dataProvider, handleApiError]);

    /**
     * Closes the modal and resets state
     */
    const closeModal = useCallback(() => {
        setModalState({
            open: false,
            loading: false,
            error: null,
            mapData: null
        });
    }, []);

    /**
     * Handles image loading errors
     */
    const handleImageError = useCallback(() => {
        setModalState(prevState => ({
            ...prevState,
            error: 'Failed to load venue map image. The image may be unavailable or corrupted.'
        }));
    }, []);

    return {
        modalState,
        handleVenueMapClick,
        closeModal,
        handleImageError
    };
};

/**
 * Main VenueMapModal component with integrated venue map handling
 * Enhanced with MUI 7 theme support for proper dark/light mode background handling
 */
const VenueMapModal: React.FC<VenueMapModalProps> = ({ modalState, onClose, onImageError }) => {
    const theme = useTheme();
    
    return (
        <Modal
            open={modalState.open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 2,
                    border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
                    p: 3,
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    outline: 'none',
                    minWidth: 400,
                    minHeight: 300
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'text.secondary',
                        bgcolor: 'background.paper',
                        '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {modalState.loading && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 250
                    }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading venue map...
                        </Typography>
                    </Box>
                )}

                {modalState.error && !modalState.loading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {modalState.error}
                        </Alert>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ mt: 2 }}
                        >
                            Close
                        </Button>
                    </Box>
                )}

                {modalState.mapData && !modalState.loading && !modalState.error && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom sx={{ pr: 5 }}>
                            {modalState.mapData.venueName}
                        </Typography>
                        <Box
                            component="img"
                            src={modalState.mapData.imageUrl}
                            alt={`Map of ${modalState.mapData.venueName}`}
                            sx={{
                                maxWidth: '100%',
                                width: '100%',
                                height: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mt: 2
                            }}
                            onError={onImageError}
                        />
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default VenueMapModal;
